/**
 * Pack Detail View
 *
 * Two-section detail view for a feature pack:
 * 1. Features — checkbox list to add/remove features with option configuration
 * 2. Projects — linked projects with unlink and bulk-link actions
 */

import { type ReactElement } from 'react';
import { useAdminPackDetail } from '../../hooks/useAdminPacks';
import { PackFeaturesSection } from './PackFeaturesSection';
import { PackProjectsSection } from './PackProjectsSection';

interface PackDetailViewProps {
  packId: string;
  onBack: () => void;
}

export function PackDetailView({ packId, onBack }: PackDetailViewProps): ReactElement {
  const { data: pack, isLoading } = useAdminPackDetail(packId);

  if (isLoading) {
    return (
      <div className="settings-loading">Loading pack details...</div>
    );
  }

  if (!pack) {
    return (
      <div className="settings-error">Pack not found.</div>
    );
  }

  return (
    <>
      <div className="page-nav">
        <button type="button" className="back-link" onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit' }}>
          &larr; Back to Packs
        </button>
      </div>
      <h2>{pack.display_name}</h2>
      {pack.description && (
        <p className="admin-page__subtitle">{pack.description}</p>
      )}

      <PackFeaturesSection
        packId={packId}
        packName={pack.display_name}
        items={pack.items}
        linkedProjectCount={pack.linked_projects.length}
      />

      <PackProjectsSection
        packId={packId}
        linkedProjects={pack.linked_projects}
      />
    </>
  );
}
